// ✅ src/pages/VehicleV2/BatteryCardMaintainPrint.jsx
// Single battery card maintenance print
// Logo: src/assets/infraLogo.jpg
//
// navigate("/vehicle/BatteryCardMaintainPrint", { state: { batteryId } })
// GET https://nissi-india.com/api/batteryCardMaintenanceReport/print/:batteryId

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import companyLogo from "../../assets/infraLogo.jpg";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB");
  return String(v);
};

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    const ddmmyy = d.toLocaleDateString("en-GB");
    const time = d
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      .toLowerCase();
    return `${ddmmyy} ${time}`;
  }
  return String(v);
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function pick(row, keys, def = "") {
  for (const k of keys) {
    const val = row?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") return val;
  }
  return def;
}

const display = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "—" : v;

export default function BatteryCardMaintainPrint() {
  const navigate = useNavigate();
  const location = useLocation();

  const batteryIdFromState = location?.state?.batteryId;
  const batteryIdFromQuery = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("batteryId");
  }, [location.search]);

  const batteryId = batteryIdFromState || batteryIdFromQuery;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  const finYear =
    typeof window !== "undefined" ? localStorage.getItem("finYear") || "" : "";

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        if (!batteryId) throw new Error("batteryId not provided");

        const res = await axios.get(`${API_BASE}/print/${batteryId}`);
        const r = res?.data?.row;

        if (!res?.data?.success || !r)
          throw new Error(res?.data?.message || "No data");

        if (alive) setRow(r);
      } catch (e) {
        if (alive)
          setErr(e?.response?.data?.message || e.message || "Failed to load print");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [batteryId]);

  const view = useMemo(() => {
    const r = row || {};

    return {
      vehicleName: toStr(
        pick(r, ["VehicleName", "fld_vehicle_name", "fld_Vehiclename", "vehicleName"])
      ),
      vehicleRegNo: toStr(
        pick(r, ["VehicleRegNo", "fld_vehicle_regno", "vehicleRegNo"])
      ),
      vehicleCode: toStr(pick(r, ["VehicleCode", "fld_vehicle_code", "vehicleCode"])),
      issuedBranch: toStr(
        pick(r, [
          "IssuedBranch",
          "fld_issuedbranch",
          "fld_IssuedBranch",
          "fld_BranchName",
        ])
      ),
      currentBranch: toStr(
        pick(r, [
          "CurrentBranch",
          "fld_currentbranch",
          "fld_CurrentBranch",
          "fld_BranchName",
        ])
      ),
      batteryMake: toStr(pick(r, ["fld_batterymake", "BatteryMake"])),
      batteryType: toStr(pick(r, ["fld_batterytype", "BatteryType"])),
      batteryCode: toStr(pick(r, ["fld_batterycode", "BatteryCode"])),
      batterySerialNo: toStr(
        pick(r, ["fld_battery_serialno", "BatterySerialNo"])
      ),
      srcSerialNo: toStr(pick(r, ["fld_src_serialno", "SrcSerialNo"])),
      supplierName: toStr(
        pick(r, ["fld_suppliername", "SupplierName", "fld_supplier"])
      ),
      fittedDate: fmtDate(pick(r, ["fld_Fitteddate", "FittedDate"])),
      currentRemoveDate: fmtDate(
        pick(r, ["CurrentRemoveDate", "removeDate", "fld_removedate", "currentdate"])
      ),
      issuedDate: fmtDate(
        pick(r, ["fld_dateofreceipt", "IssuedDate", "fld_issueddate"])
      ),
      lifeDays: toStr(pick(r, ["fld_currentlife", "LifeDays", "fld_life"])),
      batteryCost: fmtMoney(
        pick(r, ["fld_purchaseamt", "BatteryCost", "purchaseAmt"])
      ),
      perDayCosting: fmtMoney(
        pick(r, ["PerDayCosting", "perDayCost", "perDayCosting", "fld_perdaycost"])
      ),
      batteryStatus: toStr(
        pick(r, ["fld_batterystatus", "BatteryStatus"], "Process")
      ),
      dateTime: fmtDateTime(new Date()),
    };
  }, [row]);

  const currentDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const leftFields = [
    ["Vehicle Name", view.vehicleName],
    ["Vehicle Reg Number", view.vehicleRegNo],
    ["Vehicle Code", view.vehicleCode],
    ["Issued Branch", view.issuedBranch],
    ["Current Branch", view.currentBranch],
    ["Battery Make", view.batteryMake],
    ["Battery Type", view.batteryType],
    ["Battery Code", view.batteryCode],
    ["Battery Serial Number", view.batterySerialNo],
  ];

  const rightFields = [
    ["Date and Time", view.dateTime],
    ["Src Serial Number", view.srcSerialNo],
    ["Supplier Name", view.supplierName],
    ["Fitted Date", view.fittedDate],
    ["Current / Remove Date", view.currentRemoveDate],
    ["Issued Date", view.issuedDate],
    ["Life (days)", view.lifeDays],
    ["Battery Cost", view.batteryCost ? `₹ ${view.batteryCost}` : ""],
    ["Per Day Costing", view.perDayCosting ? `₹ ${view.perDayCosting}` : ""],
    ["Battery Status", view.batteryStatus],
  ];

  return (
    <div className="report-page w-full bg-white">
      <style>{`
        :root {
          --navy: #163a5f;
          --navy-deep: #0f2a45;
          --gold: #e8a317;
          --slate: #5b6b82;
          --line: #c5ced8;
          --ink: #1c2430;
        }

        .report-page {
          color: var(--ink);
          font-family: "Segoe UI", Calibri, Arial, sans-serif;
        }

        .letterhead {
          border: 1.5px solid var(--navy);
          overflow: hidden;
          background: #fff;
        }

        .letterhead-topbar {
          height: 6px;
          background: linear-gradient(90deg, #0f2a45 0%, #163a5f 55%, #e8a317 100%);
        }

        .letterhead-body {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 10px 14px 8px;
        }

        .logo-wrap {
          flex: 0 0 auto;
          width: 82px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-wrap img {
          max-width: 82px;
          max-height: 70px;
          object-fit: contain;
          display: block;
        }

        .company-block {
          flex: 1 1 auto;
          text-align: center;
          min-width: 0;
        }

        .brand-name {
          font-size: 20px;
          letter-spacing: 1.4px;
          font-weight: 800;
          color: var(--navy);
          line-height: 1.1;
          margin: 0;
        }

        .legal-name {
          margin: 2px 0 0;
          font-size: 12px;
          font-weight: 600;
          color: #2c3e50;
        }

        .addr-line {
          margin: 3px 0 0;
          font-size: 10.5px;
          color: #4b5563;
          line-height: 1.4;
        }

        .meta-block {
          flex: 0 0 150px;
          text-align: right;
          font-size: 10.5px;
          color: #334155;
          line-height: 1.35;
        }

        .meta-label {
          display: block;
          font-size: 8.5px;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--slate);
          font-weight: 700;
        }

        .meta-value {
          display: block;
          font-weight: 700;
          color: var(--navy);
        }

        .letterhead-bottombar {
          height: 3px;
          background: var(--gold);
        }

        .doc-title-wrap {
          margin: 14px 0 10px;
          text-align: center;
        }

        .doc-title {
          margin: 0;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 1.2px;
          color: var(--navy);
        }

        .doc-sub {
          margin: 2px 0 0;
          font-size: 10.5px;
          color: var(--slate);
        }

        .title-rule {
          width: 80px;
          height: 3px;
          background: var(--gold);
          margin: 6px auto 0;
        }

        .id-strip {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border: 1px solid var(--line);
          background: #f6f8fb;
          margin-bottom: 12px;
        }

        .id-cell {
          padding: 7px 10px;
          border-right: 1px solid var(--line);
        }

        .id-cell:last-child {
          border-right: none;
        }

        .id-k {
          display: block;
          font-size: 8.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: var(--slate);
        }

        .id-v {
          display: block;
          margin-top: 1px;
          font-size: 12px;
          font-weight: 700;
          color: var(--navy);
        }

        .section-title {
          background: var(--navy);
          color: #fff;
          font-size: 11px;
          letter-spacing: 0.8px;
          font-weight: 700;
          padding: 6px 10px;
          text-transform: uppercase;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          border: 1px solid var(--line);
          border-top: none;
          margin-bottom: 14px;
        }

        .detail-col + .detail-col {
          border-left: 1px solid var(--line);
        }

        .kv {
          width: 100%;
          border-collapse: collapse;
        }

        .kv td {
          padding: 6px 10px;
          font-size: 11.5px;
          vertical-align: top;
          border-bottom: 1px solid #e6ebf1;
        }

        .kv tr:last-child td {
          border-bottom: none;
        }

        .kv td.k {
          width: 42%;
          font-weight: 700;
          color: #334155;
          white-space: nowrap;
        }

        .kv td.sep {
          width: 12px;
          color: #94a3b8;
          padding-left: 0;
          padding-right: 0;
        }

        .kv td.v {
          color: #0f172a;
          font-weight: 500;
          word-break: break-word;
        }

        .kv tr:nth-child(even) td {
          background: #f7f9fc;
        }

        .test-wrap {
          border: 1px solid var(--line);
          margin-bottom: 8px;
        }

        .test-table {
          width: 100%;
          border-collapse: collapse;
        }

        .test-table th {
          background: var(--navy);
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          padding: 6px 4px;
          border: 1px solid var(--navy-deep);
          text-align: center;
        }

        .test-table td {
          border: 1px solid #d0d7e0;
          height: 42px;
          font-size: 11px;
          text-align: center;
        }

        .sign-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 22px;
          gap: 24px;
        }

        .gen-by {
          font-size: 11px;
          color: #475569;
          line-height: 1.45;
        }

        .sign-box {
          width: 200px;
          text-align: center;
        }

        .sign-line {
          border-top: 1px solid #64748b;
          padding-top: 5px;
          font-size: 11px;
          font-weight: 700;
          color: var(--navy);
        }

        .sign-hint {
          font-size: 9.5px;
          color: #64748b;
          margin-top: 2px;
        }

        .rpt-footer {
          margin-top: 14px;
          border-top: 1px solid var(--line);
          padding-top: 7px;
          display: flex;
          justify-content: space-between;
          font-size: 9.5px;
          color: #64748b;
        }

        .disclaimer {
          text-align: center;
          font-size: 9.5px;
          color: #64748b;
          margin-top: 6px;
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm 10mm 12mm 10mm;
          }

          html,
          body {
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .no-print {
            display: none !important;
          }

          .report-sheet {
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .letterhead,
          .id-strip,
          .detail-grid,
          .test-wrap,
          .sign-row {
            break-inside: avoid;
          }

          .section-title,
          .test-table th {
            background: #163a5f !important;
            color: #fff !important;
          }

          .kv tr:nth-child(even) td {
            background: #f7f9fc !important;
          }
        }
      `}</style>

      <div className="no-print flex items-center justify-between p-3 border-b">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border rounded hover:bg-gray-50"
          type="button"
        >
          ← Back
        </button>
        <button
          onClick={() => window.print()}
          className="px-6 py-2 bg-[#163a5f] hover:bg-[#0f2a45] text-white font-semibold rounded"
          type="button"
        >
          Print Report
        </button>
      </div>

      <div className="report-sheet max-w-[820px] mx-auto p-4 print:max-w-none print:mx-0 print:p-0">
        {loading && (
          <div className="no-print mb-3 p-3 border rounded bg-gray-50 text-sm">
            Loading...
          </div>
        )}
        {err && (
          <div className="no-print mb-3 p-3 border rounded bg-red-50 text-sm text-red-700">
            {err}
          </div>
        )}

        <header className="letterhead">
          <div className="letterhead-topbar" />
          <div className="letterhead-body">
            <div className="logo-wrap">
              <img
                src={companyLogo}
                alt="INFRACONSOFT"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="company-block">
              <h1 className="brand-name">INFRACONSOFT</h1>
              <p className="legal-name">
                Nissi Infotech &nbsp;|&nbsp; Nissi Civil Construction
              </p>
              <p className="addr-line">
                3/42-12, Nagappa Colony, Kavundampalayam — 641030, Tamil Nadu, India
                <br />
                Phone: +91 90037 44644 &nbsp;·&nbsp;
                infraconsoft-implementation@nissiinfotech.in
              </p>
            </div>
            <div className="meta-block">
              <span className="meta-label">Report Date</span>
              <span className="meta-value">{currentDate}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Financial Year
              </span>
              <span className="meta-value">{finYear || "—"}</span>
              <span className="meta-label" style={{ marginTop: 7 }}>
                Document
              </span>
              <span className="meta-value">BCM / Card Print</span>
            </div>
          </div>
          <div className="letterhead-bottombar" />
        </header>

        <div className="doc-title-wrap">
          <h2 className="doc-title">BATTERY CARD MAINTENANCE REPORT</h2>
          <p className="doc-sub">Single battery card — fitment and costing particulars</p>
          <div className="title-rule" />
        </div>

        <section className="id-strip">
          <div className="id-cell">
            <span className="id-k">Battery ID</span>
            <span className="id-v">{batteryId || "—"}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Battery Code</span>
            <span className="id-v">{display(view.batteryCode)}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Serial No</span>
            <span className="id-v">{display(view.batterySerialNo)}</span>
          </div>
          <div className="id-cell">
            <span className="id-k">Status</span>
            <span className="id-v">{display(view.batteryStatus)}</span>
          </div>
        </section>

        <div className="section-title">Vehicle &amp; Battery Particulars</div>
        <div className="detail-grid">
          <div className="detail-col">
            <table className="kv">
              <tbody>
                {leftFields.map(([label, value]) => (
                  <tr key={label}>
                    <td className="k">{label}</td>
                    <td className="sep">:</td>
                    <td className="v">{display(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="detail-col">
            <table className="kv">
              <tbody>
                {rightFields.map(([label, value]) => (
                  <tr key={label}>
                    <td className="k">{label}</td>
                    <td className="sep">:</td>
                    <td className="v">{display(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="section-title">Battery Testing Report</div>
        <div className="test-wrap">
          <table className="test-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ width: "22%" }}>
                  Description
                </th>
                <th>Voltmeter test</th>
                <th colSpan={6}>Hydrometer test / Specific gravity test</th>
              </tr>
              <tr>
                <th>Voltage</th>
                <th>Cell 1</th>
                <th>Cell 2</th>
                <th>Cell 3</th>
                <th>Cell 4</th>
                <th>Cell 5</th>
                <th>Cell 6</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="sign-row">
          <div className="gen-by">
            <strong style={{ color: "#163a5f" }}>Prepared by</strong>
            <br />
            Infraconsoft ERP
            <br />
            Automated battery card print
            <br />
            Generated: {view.dateTime}
          </div>
          <div className="sign-box">
            <div style={{ height: 40 }} />
            <div className="sign-line">Authorized Signatory</div>
            <div className="sign-hint">Stores / Fleet Administration</div>
          </div>
        </div>

        <div className="rpt-footer">
          <span>
            INFRACONSOFT · ERP Software · www.infraconsoft.com · owned by
            www.nissiinfotech.com
          </span>
          <span>Confidential — Internal Use Only</span>
        </div>
        <p className="disclaimer">
          This is a computer-generated report. No physical signature is required
          for authenticity unless mandated by company policy.
        </p>
      </div>
    </div>
  );
}